import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Tooltip.scss";

const VIEWPORT_MARGIN = 8;
const TOOLTIP_GAP = 8;

function resolvePosition(trigger, tooltipEl, placement) {
  const rect = trigger.getBoundingClientRect();
  const tw = tooltipEl?.offsetWidth ?? 0;
  const th = tooltipEl?.offsetHeight ?? 0;

  let top = 0;
  let left = 0;

  switch (placement) {
    case "bottom":
      top = rect.bottom + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case "left":
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.left - tw - TOOLTIP_GAP;
      break;
    case "right":
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.right + TOOLTIP_GAP;
      break;
    default:
      top = rect.top - th - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  left = Math.min(Math.max(left, VIEWPORT_MARGIN), vw - VIEWPORT_MARGIN - tw);
  top = Math.min(Math.max(top, VIEWPORT_MARGIN), vh - VIEWPORT_MARGIN - th);

  return { top, left };
}

export default function Tooltip({
  content,
  title,
  placement = "top",
  children,
  disabled = false,
  onlyWhenTruncated = false,
  ellipsis = false,
  className = "",
}) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipText = content ?? title ?? "";

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    setCoords(
      resolvePosition(triggerRef.current, tooltipRef.current, placement)
    );
  }, [placement]);

  const showTooltip = useCallback(() => {
    if (disabled || !tooltipText) return;

    const trigger = triggerRef.current;
    if (!trigger) return;

    if (onlyWhenTruncated && trigger.scrollWidth <= trigger.clientWidth) {
      return;
    }

    setVisible(true);
  }, [disabled, onlyWhenTruncated, tooltipText]);

  const hideTooltip = useCallback(() => {
    setVisible(false);
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
  }, [visible, tooltipText, updatePosition]);

  useEffect(() => {
    if (!visible) return;

    const handleReposition = () => updatePosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [visible, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className={`app-tooltip-trigger${
          ellipsis ? " app-tooltip-trigger--ellipsis" : ""
        } ${className}`.trim()}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </span>
      {visible &&
        tooltipText &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`app-tooltip app-tooltip--${placement}`}
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            {tooltipText}
          </div>,
          document.body
        )}
    </>
  );
}
